import axios, { AxiosRequestConfig } from 'axios';

declare const grecaptcha;

type Settings = {
  /**
   * Use the public collection endpoint (requires passing the google captcha site key)
   */
  publicEndpoint?: {
    captchaSiteKey: string;
  };
};

export const extractUtmTags = (options = { includePrefix: false }): { [key in string]: string } => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const sourceTags = Object.entries(Object.fromEntries(urlSearchParams.entries())).reduce(
    (prev, [key, value]) =>
      key.match(/^utm_/)
        ? {
            ...prev,
            [options.includePrefix ? key : key.replace(/^utm_/i, '')]: value,
          }
        : prev,
    {},
  );
  return sourceTags;
};

/**
 * Hook that returns a function used to post analytics collection api
 */
export const useInternalAnalytics = (): ((payload: EventPayload, settings?: Settings) => Promise<string>) => {
  return async (payload, settings = {}) => {
    let request: AxiosRequestConfig | null = null;
    let captchaToken: string | undefined;
    try {
      // Auto-extract any UTM tags from the page URL
      const sourceTags = extractUtmTags();

      // If we're using the public endpoint, a captcha token is required
      const { publicEndpoint } = settings;
      if (publicEndpoint) {
        await new Promise(resolve => grecaptcha.ready(resolve));
        captchaToken = await grecaptcha.execute(publicEndpoint.captchaSiteKey, {
          action: 'postAnalytics',
        });
      }

      const endpointUrl = '/api/v3/insights/facts';
      const publicEndpointUrl = '/api/v3/public/insights/facts';

      request = {
        method: 'POST',
        url: publicEndpoint ? publicEndpointUrl : endpointUrl,
        data: {
          captchaToken,
          sourceTags,
          ...payload,
        },
      };

      const {
        data: { data: eventId },
      } = await axios.request(request);

      return eventId;
    } catch (err) {
      console.error('Failure processing event', {
        request,
        err,
      });
    }
  };
};

type EventPayload =
  | {
      property: 'ui:fusion-app';
      eventType: 'companyProfile:view';
      eventDetails: {
        company: {
          id: number;
        };
      };
    }
  | {
      property: 'ui:discover-public';
      eventType: 'companyProfile:view';
      eventDetails: {
        company: {
          id: number;
        };
      };
    }
  // Supported by the api but not used anywhere because we
  // don't currently allow searching company profiles
  // | {
  //     eventType: 'companyProfile:search';
  //     eventDetails: {
  //       query: {
  //         filters: {
  //           [filter: string]: any;
  //         };
  //         resultCount: number;
  //       };
  //     };
  //   }
  | {
      property: 'ui:fusion-app';
      eventType: 'discoverCampaign:view';
      eventDetails: {
        discoverCampaign: {
          id: number;
        };
      };
    }
  | {
      property: 'ui:discover-public';
      eventType: 'discoverCampaign:view';
      eventDetails: {
        discoverCampaign: {
          id: number;
        };
      };
    }
  | {
      property: 'ui:fusion-app';
      eventType: 'discoverCampaign:search';
      eventDetails: {
        query: {
          filters: {
            [filter: string]: any;
          };
          resultCount: number;
        };
      };
    }
  // Supported by the api but not used anywhere yet
  // | {
  //     eventType: 'discoverSubmission:view';
  //     eventDetails: {
  //       discoverSubmission: {
  //         id: string;
  //       };
  //     };
  //   }
  | {
      property: 'ui:companies';
      eventType: 'technology:view' | 'biotechAsset:view';
      eventDetails: {
        opportunity: {
          id: string;
        };
      };
    }
  | {
      property: 'ui:portals';
      eventType: 'technology:view';
      eventDetails: {
        technology: {
          id: string;
        };
        university: {
          id: number;
        };
        opportunity?: {
          id: string;
        };
      };
    }
  | {
      property: 'ui:portals';
      eventType: 'biotechAsset:view';
      eventDetails: {
        opportunity: {
          id: string;
        };
      };
    }
  | {
      property: 'ui:companies';
      eventType: 'technology:search';
      eventDetails: {
        query: {
          filters: {
            [filter: string]: any;
          };
          resultCount: number;
        };
      };
    }
  | {
      property: 'ui:portals';
      eventType: 'technology:search';
      eventDetails: {
        query: {
          filters: {
            [filter: string]: any;
          };
          resultCount: number;
        };
        university: {
          id: number;
        };
      };
    }
  | {
      property: 'ui:companies';
      eventType: 'university:view';
      eventDetails: {
        university: {
          id: number;
        };
      };
    }
  | {
      property: 'ui:portals';
      eventType: 'university:view';
      eventDetails: {
        university: {
          id: number;
        };
      };
    }
  | {
      property: 'ui:portals';
      eventType: 'companyProfile:view';
      eventDetails: {
        company: {
          id: number;
        };
      };
    }
  | {
      property: 'ui:fusion-app';
      eventType: 'registration:view';
      eventDetails: {
        user: {
          preregistrationId?: string;
        };
      };
    }
  | {
      property: 'ui:consent';
      eventType: 'registration:view';
      eventDetails: {
        /** Discover campaign the page is showing submissions for */
        discoverCampaign?: {
          id: number;
        };

        /** List of submissions on the page (if any) */
        discoverSubmissions?: {
          id: string;
        }[];

        /** Any preregistration details if they exist */
        user?: {
          preregistrationId?: string;
        };
      };
    }
  | {
      property: 'ui:fusion-app';
      eventType: 'campaign:submit';
      eventDetails: {
        campaign: {
          id: number | string;
        };
        submission?: {
          id: string;
        };
      };
    }
  | {
      property: 'ui:fusion-app';
      eventType: 'gallery:view';
      eventDetails: {
        gallery: {
          id: string;
          galleryCampaignId: string | null;
          ongoingConnectCampaignIds: string[];
        };
      };
    }
  | {
      property: 'ui:fusion-app';
      eventType: 'gallery:join';
      eventDetails: {
        gallery: {
          id: string;
        };
        galleryMembershipId: {
          id: number;
        };
      };
    }
  | {
      property: 'ui:fusion-app';
      eventType: 'gallery:leave';
      eventDetails: {
        gallery: {
          id: string;
        };
        galleryMembershipId: {
          id: number;
        };
      };
    };
